"use client";

import { motion } from "framer-motion";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { Container } from "@/components/common/Container";
import { FounderTrustPanel } from "@/components/common/FounderTrustPanel";
import { LeadForm } from "@/components/forms/LeadForm";
import { Layout } from "@/components/layout/Layout";
import { site } from "@/content/site";

export default function ContactPage() {
  return (
    <Layout title="Contact Swargaseema | Book Site Visit">
      <main>
        <PageHero eyebrow="Contact" title="Book a private site visit." copy="Use the form or call directly to discuss project availability, layout details, and route planning." />
        <section className="py-16 lg:py-24">
          <Container className="grid items-start gap-8 lg:grid-cols-[0.85fr_1.15fr]">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="grid gap-6"
            >
              <div className="rounded-lg border border-[var(--line)] bg-[var(--white)] p-6 shadow-[0_12px_34px_rgba(23,60,49,0.07)]">
                <h2 className="font-serif text-3xl font-medium text-[var(--ink)]">Contact info</h2>
                <div className="mt-6 grid gap-5 text-sm leading-7 text-[var(--muted)]">
                  <ContactLine icon={Phone}>
                    <a href={`tel:${site.phone.replaceAll(" ", "")}`}>{site.phone}</a>
                  </ContactLine>
                  <ContactLine icon={Mail}>
                    <a href={`mailto:${site.email}`}>{site.email}</a>
                  </ContactLine>
                  <ContactLine icon={Clock}>9:30 AM to 6:30 PM</ContactLine>
                  <ContactLine icon={MapPin}>{site.address}</ContactLine>
                </div>
              </div>
              <FounderTrustPanel
                compact
                image="/brand/founder-contact-gesture.webp"
                title="A direct conversation before your visit."
                copy="Share your requirement and the team can help with project choice, travel timing, and availability."
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <LeadForm />
            </motion.div>
          </Container>
        </section>
      </main>
    </Layout>
  );
}

import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";

function ContactLine({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <p className="flex items-start gap-3">
      <Icon size={18} className="mt-1 shrink-0 text-[var(--leaf)]" /> <span>{children}</span>
    </p>
  );
}

function PageHero({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return (
    <section className="bg-[var(--ink)] pb-20 pt-32 text-[var(--white)] lg:pt-40">
      <Container>
        <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-[var(--gold-strong)]">{eyebrow}</p>
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl break-words font-serif text-3xl font-medium leading-tight sm:text-5xl lg:text-6xl sm:leading-none"
        >
          {title}
        </motion.h1>
        <p className="mt-5 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">{copy}</p>
      </Container>
    </section>
  );
}
